import React, { useState, useEffect } from "react";
import styled from "styled-components";
import queryString from "query-string";

interface Props {
  setVendor: (
    vendor: "sprint" | "verizon" | "asurion" | "att" | "staples" | "cricket"
  ) => void;
  vendor: "sprint" | "verizon" | "asurion" | "att" | "staples" | "cricket";
  articleId: string | null;
}

const VendorSwitching: React.FC<Props> = ({
  articleId,
  setVendor,
  vendor,
}: Props): React.ReactElement => {
  const Root = styled.div`
    display: block;
    position: relative;
    width: 100%;
    max-width: 1440px;
    margin: 40px auto 0;

    p {
      display: block;
      border-bottom: 2px solid #111;
    }

    select {
      display: block;
      width: 300px;
      margin-top: 4px;
      background: #fbfbfb;
      height: 32px;
      border: 2px solid #111;
      font-weight: bold;
    }

    label {
      display: block;
      position: relative;
      font-size: 14px;
      font-weight: bold;
      margin-top: 15px;
    }
  `;

  const CopyContainer = styled.div`
    display: flex;
    flex-flow: row no-wrap;
    justify-content: flex-start;
    align-items: center;
    margin-top: 20px;
  `;

  const Button = styled.button`
    display: block;
    position: relative;
    padding: 8px 12px;
    background-color: #fff;
    border: 1px solid #111;
    border-radius: 4px;
    font-size: 14px;
    font-weight: bold;
    color: #111;
    transition: all 250ms ease;
    &:hover {
      background-color: #111;
      border: 1px solid #111;
      color: #fff;
    }
    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  `;

  const CopyStatus = styled.div`
    display: block;
    position: relative;
    font-size: 16px;
  `;

  const availableVendors: { label: string; value: string }[] = [
    { label: "Asurion / Agnostic", value: "asurion" },
    { label: "Verizon", value: "verizon" },
    { label: "Sprint", value: "sprint" },
    { label: "AT&T", value: "att" },
    { label: "Staples", value: "staples" },
    { label: "Cricket", value: "cricket" },
  ];

  useEffect((): void => {
    const queryParams = queryString.parse(window.location.search);
    setVendor(
      queryParams.vendor as
        | "sprint"
        | "verizon"
        | "asurion"
        | "att"
        | "staples"
        | "cricket"
    );
  }, []);

  const [clipboardStatus, setClipboardStatus] = useState("unset");
  const [showClipboardStatus, setShowClipboardStatus] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const currentUrl: string = window.location.href;
    const newUrl: string =
      currentUrl.substring(0, currentUrl.indexOf("?")) +
      `?vendor=${e.target.value}` +
      "&preview=true";
    window.location.replace(newUrl);
  };

  const dispatchNotification = (): void => {
    setShowClipboardStatus(true);
    setTimeout((): void => {
      setShowClipboardStatus(false);
    }, 2500);
  };

  const handleCopy = (): void => {
    const domain: string = `${window.location.host}/article` as string;
    const id: string = `/${articleId}` as string;
    const queryStrings: string = `?vendor=${vendor}` as string;
    const URL: string = "https://" + domain + id + queryStrings;

    navigator.clipboard.writeText(URL).then(
      (): void => {
        setClipboardStatus("set");
        dispatchNotification();
      },
      (err): void => {
        setClipboardStatus("error");
        dispatchNotification();
        console.error("Could not copy text: ", err); // eslint-disable-line no-console
      }
    );
  };

  return (
    <Root>
      <p>Current vendor preview: 📲 {vendor} </p>
      <label htmlFor="vendorSwitch">Switch Vendor</label>
      <select
        id="vendorSwitch"
        defaultValue={undefined}
        onChange={handleChange}
      >
        {availableVendors.map(
          (vendorOption, i): Record<string, any> => {
            return (
              <option
                selected={vendor === vendorOption.value}
                key={i}
                value={vendorOption.value}
              >
                {vendorOption.label}
              </option>
            );
          }
        )}
      </select>
      <CopyContainer>
        <Button onClick={handleCopy} disabled={!articleId}>
          Copy preview link
        </Button>
        <CopyStatus>
          {showClipboardStatus && clipboardStatus === "set"
            ? "👍 Preview Link Copied"
            : null}
          {showClipboardStatus && clipboardStatus === "error"
            ? "🚨 Something went wrong copying the link"
            : null}
        </CopyStatus>
      </CopyContainer>
    </Root>
  );
};

export default VendorSwitching;
